import { useEffect, useState } from "react";

import { Encounter } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { readEncounterHistory } from "@/encounters/utils/encounter-detail-version-diff";
import { resolveEncounterSignedAt } from "@/encounters/utils/encounter-signed-at";
import { Status } from "@/encounters/zod-schemas/encounters";
import { captureExceptionWithoutPhi } from "@/utils/sentry";

export interface EncounterSignedAtState {
  /** ISO instant the encounter was signed and locked; undefined until resolved or when unknown. */
  signedAt?: string;
  /** True while the Encounter history read is in flight for a finished encounter. */
  isLoading: boolean;
}

/**
 * Resolves when a finished encounter was signed and locked, from its version history.
 *
 * `meta.lastUpdated` moves with every later write to the Encounter, so it cannot stand in for the
 * signing date. The history read is keyed on the encounter id and status only: writes to an
 * already-finished encounter never move its signing instant, so they do not trigger a re-read.
 *
 * On a failed read `signedAt` settles to undefined, so callers show no date rather than a wrong one.
 *
 * @param encounter Encounter whose signing instant to resolve. Nothing is read unless it is finished.
 */
export function useEncounterSignedAt(encounter: Encounter | undefined): EncounterSignedAtState {
  const medplum = useMedplum();
  const encounterId = encounter?.id;
  const isFinished = encounter?.status === Status.Finished;
  const [resolved, setResolved] = useState<{ encounterId: string; signedAt?: string }>();

  useEffect(() => {
    if (!encounterId || !isFinished) return;

    let isCurrent = true;

    const loadSignedAt = async () => {
      try {
        const history = await readEncounterHistory(medplum, encounterId);
        if (isCurrent) setResolved({ encounterId, signedAt: resolveEncounterSignedAt(history) });
      } catch (error) {
        captureExceptionWithoutPhi("Failed to read encounter history for its signed date", error, {
          encounterId,
        });
        if (isCurrent) setResolved({ encounterId });
      }
    };

    void loadSignedAt();

    return () => {
      isCurrent = false;
    };
  }, [encounterId, isFinished, medplum]);

  if (!encounterId || !isFinished) return { isLoading: false };
  if (resolved?.encounterId !== encounterId) return { isLoading: true };
  return { signedAt: resolved.signedAt, isLoading: false };
}
